import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export function useBrowserNotifications() {
  const { user } = useAuth();
  const [permission, setPermission] = useState<NotificationPermission>(
    typeof Notification !== "undefined" ? Notification.permission : "denied"
  );
  const [enabled, setEnabled] = useState(false);
  const prefLoadedRef = useRef(false);

  // Load browser_push_enabled pref
  useEffect(() => {
    if (!user || prefLoadedRef.current) return;
    prefLoadedRef.current = true;
    supabase
      .from("notification_preferences")
      .select("browser_push_enabled")
      .eq("user_id", user.id)
      .single()
      .then(({ data }) => {
        if (data) setEnabled(!!(data as any).browser_push_enabled);
      });
  }, [user]);

  const requestPermission = useCallback(async () => {
    if (typeof Notification === "undefined") return "denied";
    const result = await Notification.requestPermission();
    setPermission(result);
    if (result === "granted" && user) {
      setEnabled(true);
      await supabase
        .from("notification_preferences")
        .update({ browser_push_enabled: true } as any)
        .eq("user_id", user.id);
    }
    return result;
  }, [user]);

  const disablePush = useCallback(async () => {
    setEnabled(false);
    if (user) {
      await supabase
        .from("notification_preferences")
        .update({ browser_push_enabled: false } as any)
        .eq("user_id", user.id);
    }
  }, [user]);

  const showBrowserNotification = useCallback(
    (title: string, body: string, onClick?: () => void) => {
      if (
        typeof Notification === "undefined" ||
        Notification.permission !== "granted" ||
        !enabled
      )
        return;

      const notif = new Notification(title, {
        body,
        icon: "/favicon.ico",
      });
      if (onClick) {
        notif.onclick = () => {
          window.focus();
          onClick();
          notif.close();
        };
      }
    },
    [enabled]
  );

  return {
    permission,
    enabled,
    requestPermission,
    disablePush,
    showBrowserNotification,
    isSupported: typeof Notification !== "undefined",
  };
}
